/**
 * AmbientPlayer — dual-track looping background audio using the Web Audio API.
 *
 * Uses AudioContext + AudioBufferSourceNode instead of HTMLAudioElement so that
 * playback is never blocked by browser autoplay policy. The context must be
 * unlocked during a user gesture (call ensureUnlocked() on submit) — before
 * async SSE callbacks attempt playback, ensureRunning() resumes a suspended ctx.
 *
 * Manages two independent looping tracks:
 *   - env:   physical environment sounds (city, park, rain, …)   vol 0.50
 *   - music: diegetic music when the scene calls for it          vol 0.30
 *
 * Both tracks fade in/out using AudioParam ramps (no setInterval needed).
 */

const FADE_IN_SEC  = 2.5;
const FADE_OUT_SEC = 1.5;
const ENV_VOLUME   = 0.50;
const MUSIC_VOLUME = 0.15;

export type AmbientUiState = 'off' | 'waiting' | 'loading' | 'playing' | 'error';

export interface AmbientPlayerOptions {
  onStateChange?: (state: AmbientUiState) => void;
}

interface TrackState {
  source: AudioBufferSourceNode | null;
  gain:   GainNode | null;
  url:    string | null;
}

export class AmbientPlayer {
  private audioCtx: AudioContext | null = null;
  private env:   TrackState = { source: null, gain: null, url: null };
  private music: TrackState = { source: null, gain: null, url: null };

  private pendingEnvUrl:   string | null = null;
  private pendingMusicUrl: string | null | undefined = undefined; // undefined = not yet received

  /** User intent — single source of truth for the mute button. */
  private userEnabled = true;
  private loadingCount = 0;
  private loadFailed = false;

  /** True once this session has received ambient URLs from the server. */
  private ambientSessionActive = false;

  private readonly onStateChange?: (state: AmbientUiState) => void;

  constructor(opts: AmbientPlayerOptions = {}) {
    this.onStateChange = opts.onStateChange;

    document.addEventListener('visibilitychange', () => {
      if (!document.hidden && this.userEnabled) {
        void this.ensureRunning().then((ok) => {
          if (ok) void this.startPending();
        });
      }
    });
  }

  getUiState(): AmbientUiState {
    if (!this.userEnabled) return 'off';
    if (this.loadFailed) return 'error';
    if (this.loadingCount > 0) return 'loading';
    if (this.isAudible()) return 'playing';
    if (this.pendingEnvUrl || this.pendingMusicUrl !== undefined) return 'waiting';
    return 'off';
  }

  /** @deprecated Prefer getUiState(). Kept for any legacy callers. */
  get isPlaying(): boolean {
    return this.isAudible();
  }

  /** Whether the ambient control should be shown (URLs received this session). */
  isAmbientSessionActive(): boolean {
    return this.ambientSessionActive;
  }

  /**
   * and unlock the AudioContext. Safe to call multiple times.
   */
  ensureUnlocked(): void {
    if (!this.audioCtx) {
      this.audioCtx = new AudioContext();
    }
    if (this.audioCtx.state === 'suspended') {
      void this.audioCtx.resume();
    }
  }

  /**
   * Call at the start of each director session (prompt submit). Clears error
   * state and unlocks the context without changing user mute preference.
   */
  prepareForSession(): void {
    this.loadFailed = false;
    this.ensureUnlocked();
    this.emitState();
  }

  setUserEnabled(on: boolean): void {
    this.userEnabled = on;
    this.emitState();
  }

  /**
   * Load and play new ambient URLs. If muted, stores URLs for when the user
   * re-enables. musicUrl may be null for scenes with no diegetic music.
   */
  play(envUrl: string, musicUrl: string | null): void {
    this.pendingEnvUrl   = envUrl;
    this.pendingMusicUrl = musicUrl;
    this.ambientSessionActive = true;
    this.loadFailed = false;
    this.emitState();
    if (!this.userEnabled) return;
    void this.startPending();
  }

  /** Enable playback — resumes context and replays pending URLs. */
  retry(): void {
    this.loadFailed = false;
    this.userEnabled = true;
    this.emitState();
    void this.startPending();
  }

  resume(): void {
    this.userEnabled = true;
    this.loadFailed = false;
    this.emitState();
    void this.startPending();
  }

  /** Mute — fade out without discarding pending URLs. */
  pause(): void {
    this.userEnabled = false;
    this.fadeOut(this.env);
    this.fadeOut(this.music);
    this.emitState();
  }

  /**
   * End the current scene — stop tracks and discard buffered URLs. Preserves
   * userEnabled so the next session honours the mute button.
   */
  stop(): void {
    this.pendingEnvUrl   = null;
    this.pendingMusicUrl = undefined;
    this.ambientSessionActive = false;
    this.loadFailed      = false;
    this.loadingCount    = 0;
    this.fadeOut(this.env);
    this.fadeOut(this.music);
    this.emitState();
  }

  /** Re-emit current UI state (e.g. after wiring the button element). */
  refreshUi(): void {
    this.emitState();
  }

  // ── Private ────────────────────────────────────────────────────────────────

  private emitState(): void {
    this.onStateChange?.(this.getUiState());
  }

  private isAudible(): boolean {
    if (this.audioCtx?.state !== 'running') return false;
    return this.env.source !== null || this.music.source !== null;
  }

  private async ensureRunning(): Promise<boolean> {
    if (!this.audioCtx) {
      console.warn(
        '[ambient] no AudioContext — call ensureUnlocked() during a user gesture first',
      );
      return false;
    }
    if (this.audioCtx.state === 'suspended') {
      try {
        await this.audioCtx.resume();
      } catch (err) {
        console.warn('[ambient] AudioContext.resume() failed:', err);
        this.loadFailed = true;
        this.emitState();
        return false;
      }
    }
    if (this.audioCtx.state !== 'running') {
      console.warn(`[ambient] AudioContext state is "${this.audioCtx.state}" — cannot play`);
      return false;
    }
    return true;
  }

  private async startPending(): Promise<void> {
    if (!this.userEnabled) return;
    if (!this.pendingEnvUrl && this.pendingMusicUrl === undefined) return;
    if (!(await this.ensureRunning())) return;

    if (this.pendingEnvUrl) {
      void this.loadAndPlay(this.env, this.pendingEnvUrl, ENV_VOLUME);
    }
    if (this.pendingMusicUrl !== undefined) {
      void this.loadAndPlay(this.music, this.pendingMusicUrl, MUSIC_VOLUME);
    }
  }

  private async loadAndPlay(
    track: TrackState,
    url: string | null,
    targetVol: number,
  ): Promise<void> {
    if (url === null) {
      this.fadeOut(track);
      this.emitState();
      return;
    }

    if (track.url === url && track.source !== null) {
      this.emitState();
      return;
    }

    this.loadingCount += 1;
    this.emitState();

    this.fadeOut(track);
    track.url = url;

    try {
      if (!(await this.ensureRunning())) {
        this.loadFailed = true;
        return;
      }

      const ctx = this.audioCtx;
      if (!ctx) return;

      const res = await fetch(url);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const arrayBuffer = await res.arrayBuffer();
      const audioBuffer = await ctx.decodeAudioData(arrayBuffer);

      if (!this.userEnabled || track.url !== url) return;

      if (!(await this.ensureRunning())) {
        this.loadFailed = true;
        return;
      }

      const gain = ctx.createGain();
      gain.gain.setValueAtTime(0, ctx.currentTime);
      gain.connect(ctx.destination);

      const source = ctx.createBufferSource();
      source.buffer = audioBuffer;
      source.loop   = true;
      source.connect(gain);
      source.start();

      track.source = source;
      track.gain   = gain;

      gain.gain.linearRampToValueAtTime(targetVol, ctx.currentTime + FADE_IN_SEC);
      this.loadFailed = false;
    } catch (err) {
      console.warn('[ambient] failed to load track:', err);
      this.loadFailed = true;
    } finally {
      this.loadingCount = Math.max(0, this.loadingCount - 1);
      this.emitState();
    }
  }

  private fadeOut(track: TrackState): void {
    const { source, gain } = track;
    if (!gain || !this.audioCtx) {
      track.source = null;
      track.gain   = null;
      return;
    }

    const ctx     = this.audioCtx;
    const endTime = ctx.currentTime + FADE_OUT_SEC;

    gain.gain.setValueAtTime(gain.gain.value, ctx.currentTime);
    gain.gain.linearRampToValueAtTime(0, endTime);

    const capturedSource = source;
    const capturedGain   = gain;
    setTimeout(() => {
      try { capturedSource?.stop(); } catch { /* already stopped */ }
      try { capturedGain.disconnect(); } catch { /* already disconnected */ }
    }, (FADE_OUT_SEC + 0.1) * 1000);

    track.source = null;
    track.gain   = null;
  }
}
